import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PrintIcon from '@mui/icons-material/Print';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getMinistries,
  createMinistry,
  createLeader,
  deleteLeader,
  updateMinistry,
  deleteMinistry,
  getMinistryMinistryAreas,
  updateMinistryAreasDisplayOrder,
  updateMinistriesDisplayOrder,
  getMembers,
  getGroups,
} from '../api';
import { useAuth } from '../context/AuthContext';

export default function MinistriesPage() {
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'add',
    item: null,
    name: '',
    description: '',
    minimum_involvements: '',
    leaders: [null, null],
    leader_member_id: null,
    leader2_member_id: null,
    leader3_member_id: null,
    is_active: true,
  });
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sortOverlay, setSortOverlay] = useState({ open: false, ministry: null, areas: [], loading: false });
  const [ministriesSortOverlay, setMinistriesSortOverlay] = useState({ open: false, items: [], loading: false });
  const [statusFilter, setStatusFilter] = useState('all');
  const [printLoading, setPrintLoading] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const sortByDisplayOrder = (a, b) => {
    const aOrder = a.display_order ?? 999;
    const bOrder = b.display_order ?? 999;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.name || '').localeCompare(b.name || '');
  };

  const handlePrint = async () => {
    const activeMinistries = ministries
      .filter((m) => m.is_active === true || m.is_active === 1)
      .sort(sortByDisplayOrder);
    if (activeMinistries.length === 0) {
      setSnackbar({ open: true, message: 'No active ministries to print', severity: 'info' });
      return;
    }
    setPrintLoading(true);
    try {
      const ministriesWithAreas = await Promise.all(
        activeMinistries.map(async (m) => {
          const areas = await getMinistryMinistryAreas(m.id, { sortBy: 'display_order', sortOrder: 'asc' });
          const sortedAreas = [...(areas || [])].sort((a, b) => {
            const aOrder = a.display_order ?? 999;
            const bOrder = b.display_order ?? 999;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return (a.name || '').localeCompare(b.name || '');
          });
          return { ...m, ministry_areas: sortedAreas };
        })
      );
      const getLeaderInfo = (m) => {
        const names =
          m.leaders?.length > 0
            ? (m.leaders || []).map((l) => {
                if (l?.group_id != null && l?.group) return l.group.name;
                if (l?.member) return `${l.member.first_name} ${l.member.last_name}`;
                return null;
              }).filter(Boolean)
            : [
                m.leader_member_id != null && m.leader && `${m.leader.first_name} ${m.leader.last_name}`,
                m.leader2_member_id != null && m.leader2 && `${m.leader2.first_name} ${m.leader2.last_name}`,
                m.leader3_member_id != null && m.leader3 && `${m.leader3.first_name} ${m.leader3.last_name}`,
              ].filter(Boolean);
        const label = names.length === 1 ? 'Ministry Leader' : 'Ministry Leaders';
        return { names, label };
      };
      const escapeHtml = (s) =>
        (s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      const html = `<!DOCTYPE html>
<html>
<head>
  <title>Ministries</title>
  <style>
    body { font-family: sans-serif; font-size: 12px; padding: 1em; }
    .print-container { column-count: 1; }
    .ministry { break-inside: avoid; margin-bottom: 1.5em; }
    h1 { font-size: 1.5em; margin: 0 0 0.25em 0; }
    h3 { font-size: 1.1em; margin: 0.75em 0 0.25em 0; }
    .leaders { font-style: italic; margin-bottom: 0.5em; }
    .description { margin: 0 0 0.5em 0; white-space: pre-wrap; }
  </style>
</head>
<body>
  <div class="print-container">
${ministriesWithAreas
  .map((m) => {
    const { names, label } = getLeaderInfo(m);
    const leadersText = names.length ? escapeHtml(names.join(', ')) : '—';
    return `    <div class="ministry">
      <h1>${escapeHtml(m.name)}</h1>
      <div class="leaders">${escapeHtml(label)}: ${leadersText}</div>
      ${m.description ? `<div class="description">${escapeHtml(m.description)}</div>` : ''}
      ${(m.ministry_areas || [])
        .map(
          (a) =>
            `      <h3>${escapeHtml(a.name)}</h3>
      ${a.description ? `<div class="description">${escapeHtml(a.description)}</div>` : ''}`
        )
        .join('')}
    </div>`;
  })
  .join('\n')}
  </div>
</body>
</html>`;
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      win.focus();
      win.print();
      win.close();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setPrintLoading(false);
    }
  };

  const load = async () => {
    try {
      const data = await getMinistries();
      setMinistries(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortBy(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
  };

  const getSortValue = (min, field) => {
    switch (field) {
      case 'name':
        return (min.name || '').toLowerCase();
      case 'display_order':
        return min.display_order ?? 999;
      case 'men':
        return min.men_count ?? 0;
      case 'women':
        return min.women_count ?? 0;
      default:
        return '';
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMembers = async () => {
    setMembersLoading(true);
    try {
      const { data } = await getMembers({ pageSize: 999, sortBy: 'last_name', sortOrder: 'asc' });
      setMembers(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setMembersLoading(false);
    }
  };

  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      const data = await getGroups({ sortBy: 'name', sortOrder: 'asc' });
      setGroups(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleOpenAdd = async () => {
    await Promise.all([loadMembers(), loadGroups()]);
    setDialog({
      open: true,
      mode: 'add',
      item: null,
      name: '',
      description: '',
      minimum_involvements: '5',
      leaders: [null, null],
      leader_member_id: null,
      leader2_member_id: null,
      leader3_member_id: null,
      is_active: true,
    });
  };

  const handleAddLeaderSlot = () => {
    setDialog((d) => ({ ...d, leaders: [...d.leaders, null] }));
  };

  const handleLeaderChange = (index, value) => {
    setDialog((d) => {
      const next = [...d.leaders];
      next[index] = value; // { type: 'member', id } or { type: 'group', id } or null
      return { ...d, leaders: next };
    });
  };

  const handleOpenEdit = async (min) => {
    await Promise.all([loadMembers(), loadGroups()]);
    const leaderEntries =
      min.leaders?.length > 0
        ? (min.leaders || []).map((l) => {
            if (l?.group_id != null || l?.group?.id != null) {
              return { type: 'group', id: l.group_id ?? l.group?.id };
            }
            if (l?.member_id != null || l?.member?.id != null) {
              return { type: 'member', id: l.member_id ?? l.member?.id };
            }
            return null;
          }).filter(Boolean)
        : [min.leader_member_id, min.leader2_member_id, min.leader3_member_id]
            .filter(Boolean)
            .map((mid) => (mid != null ? { type: 'member', id: mid } : null))
            .filter(Boolean);
    const leaders = leaderEntries.length >= 2 ? leaderEntries : [...leaderEntries, ...Array(2 - leaderEntries.length).fill(null)];
    setDialog({
      open: true,
      mode: 'edit',
      item: min,
      name: min.name,
      description: min.description ?? '',
      minimum_involvements: min.minimum_involvements != null ? String(min.minimum_involvements) : '',
      leaders,
      existingLeaders: min.leaders || [],
      leader_member_id: null,
      leader2_member_id: null,
      leader3_member_id: null,
      is_active: min.is_active !== false,
    });
  };

  const handleCloseDialog = () => setDialog({ ...dialog, open: false });

  const handleSave = async () => {
    try {
      const basePayload = {
        name: dialog.name.trim(),
        description: dialog.description.trim() || null,
        ...(dialog.minimum_involvements !== ''
          ? { minimum_involvements: parseInt(dialog.minimum_involvements, 10) }
          : dialog.mode === 'add'
            ? { minimum_involvements: 5 }
            : {}),
        is_active: dialog.is_active,
      };
      const leaderPayload = (entry) => {
        if (!entry || !entry.id) return null;
        if (entry.type === 'group') {
          return { group_id: entry.id };
        }
        return { member_id: entry.id };
      };

      if (dialog.mode === 'add') {
        const ministryPayload = { ...basePayload };
        const ministry = await createMinistry(ministryPayload);
        const leaderEntries = (dialog.leaders || []).filter(Boolean);
        for (const entry of leaderEntries) {
          const payload = leaderPayload(entry);
          if (payload) {
            await createLeader({ ...payload, ministry_id: ministry.id });
          }
        }
        setSnackbar({ open: true, message: 'Ministry created', severity: 'success' });
      } else {
        await updateMinistry(dialog.item.id, basePayload);
        const ministryId = dialog.item.id;
        const currentEntries = new Map(
          (dialog.leaders || [])
            .filter(Boolean)
            .map((e) => [`${e.type}:${e.id}`, e])
        );
        const existing = dialog.existingLeaders || [];
        for (const leader of existing) {
          const key = leader.group_id != null
            ? `group:${leader.group_id}`
            : `member:${leader.member_id ?? leader.member?.id}`;
          if (!currentEntries.has(key)) {
            await deleteLeader(leader.id);
          }
        }
        const existingKeys = new Set(
          existing.map((l) =>
            l.group_id != null ? `group:${l.group_id}` : `member:${l.member_id ?? l.member?.id}`
          )
        );
        for (const entry of currentEntries.values()) {
          const key = `${entry.type}:${entry.id}`;
          if (!existingKeys.has(key)) {
            const payload = leaderPayload(entry);
            if (payload) {
              await createLeader({ ...payload, ministry_id: ministryId });
            }
          }
        }
        setSnackbar({ open: true, message: 'Ministry updated', severity: 'success' });
      }
      handleCloseDialog();
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleDelete = async (min) => {
    setDeleteConfirm(min);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteMinistry(deleteConfirm.id);
      setSnackbar({ open: true, message: 'Ministry deleted', severity: 'success' });
      setDeleteConfirm(null);
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleViewMinistryAreas = (min) => {
    navigate(`/admin/ministry-areas?ministry=${min.id}`);
  };

  const handleViewInvolvements = (min) => {
    navigate(`/admin/preferences/ministries/${min.id}`);
  };

  const handleOpenSort = async (min) => {
    setSortOverlay({ open: true, ministry: min, areas: [], loading: true });
    try {
      const areas = await getMinistryMinistryAreas(min.id, { sortBy: 'display_order', sortOrder: 'asc' });
      const sorted = [...areas].sort((a, b) => {
        const aOrder = a.display_order ?? 999;
        const bOrder = b.display_order ?? 999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      });
      setSortOverlay((prev) => ({ ...prev, areas: sorted, loading: false }));
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
      setSortOverlay((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseSortOverlay = () => {
    setSortOverlay({ open: false, ministry: null, areas: [], loading: false });
  };

  const handleSortDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    e.target.style.opacity = '0.5';
  };

  const handleSortDragEnd = (e) => {
    e.target.style.opacity = '1';
  };

  const handleSortDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSortDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;
    setSortOverlay((prev) => {
      const next = [...prev.areas];
      const [removed] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, removed);
      return { ...prev, areas: next };
    });
  };

  const handleSubmitSort = async () => {
    if (!sortOverlay.ministry || sortOverlay.areas.length === 0) return;
    try {
      const areas = sortOverlay.areas.map((a, i) => ({
        ministry_area_id: a.id,
        display_order: i,
      }));
      await updateMinistryAreasDisplayOrder(sortOverlay.ministry.id, areas);
      setSnackbar({ open: true, message: 'Display order updated', severity: 'success' });
      handleCloseSortOverlay();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleOpenMinistriesSort = () => {
    const sorted = [...ministries].sort((a, b) => {
      const aOrder = a.display_order ?? 999;
      const bOrder = b.display_order ?? 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.name || '').localeCompare(b.name || '');
    });
    setMinistriesSortOverlay({ open: true, items: sorted, loading: false });
  };

  const handleCloseMinistriesSortOverlay = () => {
    setMinistriesSortOverlay({ open: false, items: [], loading: false });
  };

  const handleMinistriesSortDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    e.target.style.opacity = '0.5';
  };

  const handleMinistriesSortDragEnd = (e) => {
    e.target.style.opacity = '1';
  };

  const handleMinistriesSortDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleMinistriesSortDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;
    setMinistriesSortOverlay((prev) => {
      const next = [...prev.items];
      const [removed] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, removed);
      return { ...prev, items: next };
    });
  };

  const handleSubmitMinistriesSort = async () => {
    if (ministriesSortOverlay.items.length === 0) return;
    try {
      setMinistriesSortOverlay((prev) => ({ ...prev, loading: true }));
      const payload = ministriesSortOverlay.items.map((m, i) => ({
        ministry_id: m.id,
        display_order: i,
      }));
      await updateMinistriesDisplayOrder(payload);
      setSnackbar({ open: true, message: 'Ministries display order updated', severity: 'success' });
      handleCloseMinistriesSortOverlay();
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
      setMinistriesSortOverlay((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">Ministries</Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Print active ministries with descriptions and ministry areas">
            <span>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                disabled={printLoading}
                sx={{ color: 'grey.600', borderColor: 'grey.400', '&:hover': { borderColor: 'grey.500', bgcolor: 'grey.100' } }}
              >
                {printLoading ? 'Loading…' : 'Print'}
              </Button>
            </span>
          </Tooltip>
          {isAdmin && (
          <>
            <Tooltip title="Edit the order that the ministries are displayed in the form app.">
              <Button
                variant="outlined"
                sx={{ color: 'grey.600', borderColor: 'grey.400', '&:hover': { borderColor: 'grey.500', bgcolor: 'grey.100' } }}
                onClick={handleOpenMinistriesSort}
              >
                Edit Display Order
              </Button>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
              Add Ministry
            </Button>
          </>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'name' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Leaders</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sortDirection={sortBy === 'display_order' ? sortOrder : false}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  <TableSortLabel
                    active={sortBy === 'display_order'}
                    direction={sortBy === 'display_order' ? sortOrder : 'asc'}
                    onClick={() => handleSort('display_order')}
                  >
                    Display Order
                  </TableSortLabel>
                  <Tooltip title="Value affects how ministries appear on the Involvement Preferences form.">
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.disabled' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right">Min. Involvements</TableCell>
              <TableCell align="right" sortDirection={sortBy === 'men' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'men'}
                  direction={sortBy === 'men' ? sortOrder : 'asc'}
                  onClick={() => handleSort('men')}
                >
                  Men
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortBy === 'women' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'women'}
                  direction={sortBy === 'women' ? sortOrder : 'asc'}
                  onClick={() => handleSort('women')}
                >
                  Women
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>Loading...</TableCell>
              </TableRow>
            ) : (
              [...ministries]
                .filter((min) => {
                  if (statusFilter === 'all') return true;
                  if (statusFilter === 'enabled') return min.is_active !== false;
                  if (statusFilter === 'disabled') return min.is_active === false;
                  return true;
                })
                .sort((a, b) => {
                  const aVal = getSortValue(a, sortBy);
                  const bVal = getSortValue(b, sortBy);
                  const cmp =
                    typeof aVal === 'number' && typeof bVal === 'number'
                      ? aVal - bVal
                      : String(aVal).localeCompare(String(bVal));
                  return sortOrder === 'asc' ? cmp : -cmp;
                })
                .map((min) => {
                const leaderNames =
                  min.leaders?.length > 0
                    ? (min.leaders || []).map((l) => {
                        if (l?.group_id != null && l?.group) return l.group.name;
                        if (l?.member) return `${l.member.first_name} ${l.member.last_name}`;
                        return null;
                      }).filter(Boolean)
                    : [
                        min.leader_member_id != null && min.leader && `${min.leader.first_name} ${min.leader.last_name}`,
                        min.leader2_member_id != null && min.leader2 && `${min.leader2.first_name} ${min.leader2.last_name}`,
                        min.leader3_member_id != null && min.leader3 && `${min.leader3.first_name} ${min.leader3.last_name}`,
                      ].filter(Boolean);
                return (
                <TableRow key={min.id}>
                  <TableCell>{min.name}</TableCell>
                  <TableCell>{leaderNames.join(', ') || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={min.is_active !== false ? 'Enabled' : 'Disabled'}
                      size="small"
                      color={min.is_active !== false ? 'success' : 'error'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell align="right">{min.display_order != null ? min.display_order + 1 : '—'}</TableCell>
                  <TableCell align="right">{min.minimum_involvements ?? ''}</TableCell>
                  <TableCell align="right">{min.men_count ?? 0}</TableCell>
                  <TableCell align="right">{min.women_count ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewMinistryAreas(min)}
                      title="View Ministry Areas"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewInvolvements(min)}
                      title="View Involvements"
                    >
                      <PeopleIcon />
                    </IconButton>
                    {isAdmin && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSort(min)}
                        title="Sort Ministry Areas"
                      >
                        <DragIndicatorIcon />
                      </IconButton>
                    )}
                    {isAdmin && (
                      <IconButton size="small" onClick={() => handleOpenEdit(min)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    {isAdmin && (
                      <IconButton size="small" color="error" onClick={() => handleDelete(min)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={handleCloseDialog}>
        <DialogTitle>{dialog.mode === 'add' ? 'Add Ministry' : 'Edit Ministry'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={dialog.name}
            onChange={(e) => setDialog({ ...dialog, name: e.target.value })}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Description"
            value={dialog.description}
            onChange={(e) => setDialog({ ...dialog, description: e.target.value })}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={dialog.is_active}
                onChange={(e) => setDialog({ ...dialog, is_active: e.target.checked })}
              />
            }
            label="Enabled"
            sx={{ mt: 2, display: 'block' }}
          />
          <TextField
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 1 }}
            label="Minimum Involvements"
            placeholder="Uses ministry default if empty"
            value={dialog.minimum_involvements}
            onChange={(e) => setDialog({ ...dialog, minimum_involvements: e.target.value })}
            sx={{ mt: 2 }}
            helperText="Minimum number of people needed per ministry area.  
            All ministry areas will default to this value.  
            Or each ministry area can override.  This value affects how the Involvement Preferences 
            form is presented.  Attention will be drawn to ministry areas where the involvement are below this number. "
          />
          {(() => {
            const memberIds = new Set((members || []).map((m) => m.id));
            const extraMembers = (dialog.item?.leaders || []).map((l) => l?.member).filter((m) => m && !memberIds.has(m.id));
            [dialog.item?.leader, dialog.item?.leader2, dialog.item?.leader3].forEach((l) => {
              if (l && !memberIds.has(l.id)) {
                extraMembers.push(l);
                memberIds.add(l.id);
              }
            });
            const memberOptions = [...(members || []), ...extraMembers].map((m) => ({
              type: 'member',
              id: m.id,
              label: `${m.first_name} ${m.last_name}`,
            }));
            const groupIds = new Set((groups || []).map((g) => g.id));
            const extraGroups = (dialog.item?.leaders || [])
              .map((l) => l?.group)
              .filter((g) => g && !groupIds.has(g.id));
            extraGroups.forEach((g) => groupIds.add(g.id));
            const groupOptions = [
              ...(groups || []),
              ...extraGroups,
            ].map((g) => ({
              type: 'group',
              id: g.id,
              label: `${g.name} (Group)`,
            }));
            const options = [...memberOptions, ...groupOptions];
            const leaders = dialog.leaders || (dialog.mode === 'add' ? [null, null] : []);
            const findOption = (entry) =>
              entry
                ? options.find((o) => o.type === entry.type && o.id === entry.id)
                : null;
            return (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Leaders
                </Typography>
                {leaders.map((entry, index) => (
                  <Autocomplete
                    key={index}
                    sx={{ mt: index === 0 ? 0 : 2 }}
                    options={options}
                    getOptionLabel={(opt) => (opt ? opt.label : '')}
                    value={findOption(entry) || null}
                    onChange={(_, v) => handleLeaderChange(index, v ? { type: v.type, id: v.id } : null)}
                    loading={membersLoading || groupsLoading}
                    renderInput={(params) => (
                      <TextField {...params} label={`Leader ${index + 1}`} placeholder="Select member or group" />
                    )}
                  />
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddLeaderSlot}
                  sx={{ mt: 2 }}
                  size="small"
                >
                  Add leader
                </Button>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!dialog.name.trim() || (dialog.minimum_involvements !== '' && (isNaN(parseInt(dialog.minimum_involvements, 10)) || parseInt(dialog.minimum_involvements, 10) < 0))}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sortOverlay.open} onClose={handleCloseSortOverlay} maxWidth="sm" fullWidth>
        <DialogTitle>Sort Ministry Areas — {sortOverlay.ministry?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag items to reorder. Order affects how ministry areas appear on the Involvement Preferences form.
          </Typography>
          {sortOverlay.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sortOverlay.areas.length === 0 ? (
            <Typography color="text.secondary">No ministry areas</Typography>
          ) : (
            <List dense disablePadding>
              {sortOverlay.areas.map((area, index) => (
                <ListItem
                  key={area.id}
                  draggable
                  onDragStart={(e) => handleSortDragStart(e, index)}
                  onDragEnd={handleSortDragEnd}
                  onDragOver={handleSortDragOver}
                  onDrop={(e) => handleSortDrop(e, index)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <DragIndicatorIcon sx={{ mr: 1, color: 'action.disabled' }} />
                  <ListItemText primary={area.name} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSortOverlay}>Cancel</Button>
          <Button
            onClick={handleSubmitSort}
            variant="contained"
            disabled={sortOverlay.loading || sortOverlay.areas.length === 0}
          >
            Save Order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ministriesSortOverlay.open} onClose={handleCloseMinistriesSortOverlay} maxWidth="sm" fullWidth>
        <DialogTitle>Sort Ministries</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag items to reorder. Order affects how ministries appear on the Involvement Preferences form.
          </Typography>
          {ministriesSortOverlay.items.length === 0 ? (
            <Typography color="text.secondary">No ministries</Typography>
          ) : (
            <List dense disablePadding>
              {ministriesSortOverlay.items.map((min, index) => (
                <ListItem
                  key={min.id}
                  draggable
                  onDragStart={(e) => handleMinistriesSortDragStart(e, index)}
                  onDragEnd={handleMinistriesSortDragEnd}
                  onDragOver={handleMinistriesSortDragOver}
                  onDrop={(e) => handleMinistriesSortDrop(e, index)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <DragIndicatorIcon sx={{ mr: 1, color: 'action.disabled' }} />
                  <ListItemText primary={min.name} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMinistriesSortOverlay}>Cancel</Button>
          <Button
            onClick={handleSubmitMinistriesSort}
            variant="contained"
            disabled={ministriesSortOverlay.loading || ministriesSortOverlay.items.length === 0}
          >
            Save Order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Ministry</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteConfirm?.name}"? This will also delete all ministry areas in this ministry.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
